import {type TreeNode, type PreviewableTreeNode, type LoadableTreeNode, LOADING_STATUS, type ProjectDiff, type MultiPageDiff, type FileDiffStatus, FILE_DIFF_STATUS} from './types/file_tree.ts';
import type {Container} from '../container.ts';

import {emptyPreview, errorPreview, loadingPreview, type Preview} from './types/previews.ts';
import {reactive, computed, ref, watch, shallowRef, triggerRef, nextTick, type Ref, type ComputedRef, type ShallowRef} from 'vue';
import {Loader, type PreviewURLs} from './loader.ts';
import {AllSpiceJsonLoader} from '../features/allspicejson.ts';
import type {DiffPreviewData} from './types/diff-preview-data.ts';
import {buildDiffPreviewRoute} from '../components/AllSpiceDiff/AllSpiceDiffViewRouteBuilder.ts';
import {timing} from '../analytics.ts';
import {mergePages} from './mergePages.ts';
import {buildSchematicHierarchy, type SchematicHierarchyGroup, type SchematicHierarchyInputPage} from './components/diff-list/schematic-hierarchy.ts';
import type {SchematicPageGroup} from '../../generated/crysknife/types.ts';
import type {DiffDisplayLevel} from './api/view-preferences.ts';
const {appSubUrl} = window.config;

// TODO: the store is currently pretty small and simple but should it get out of handle we should reach for pinia

export type DiffInfo = {
  baseCommit: string | null,
  headCommit: string,
  issueLink: string,
  reponame: string,
  username: string,
};

export type PRInfo = {
  filesViewedState: Record<string, number>,
  number: number,
};

export type RawWarning = {
  file_path: string;
  detail: string;
};

export type DiffFilterConfig = {
  diffToggle: Ref<DiffDisplayLevel>;
  filterOverrides: Ref<Record<string, boolean>>;
  layerOverrides: Ref<Record<number, boolean>>;
  showUnchangedSheets: Ref<boolean>;
};

export type DiffFilterConfigData = {
  diffToggle?: string;
  filterOverrides?: Record<string, boolean>;
  layerOverrides?: Record<string, boolean>;
  showUnchangedSheets?: boolean;
};

export type Store = {
  activeHash: ComputedRef<string>;
  activePath: Ref<string>;
  activePreview: Ref<Preview>;
  container: Container;
  diffInfo: DiffInfo,
  diffFilterConfig: DiffFilterConfig;
  fileTree: {items: TreeNode[]};
  hashToPathMap: ComputedRef<Map<string, PreviewableTreeNode>>;
  activeHierarchy: ComputedRef<SchematicHierarchyGroup[]>;
  isProjectView: Ref<boolean>;
  issueLink: string;
  loader: ShallowRef<Loader>;
  prInfo: PRInfo,
  repoLink: string;
  refLinkSnippet: string;
  suppressNextAnchorScroll: Ref<boolean>;
  warnings: RawWarning[];
  diffFilterHiddenIds: Ref<string[]>;
};

const VALID_TOGGLE_KEYS = new Set(['full', 'added', 'removed', 'no']);

const createStore = (initialFileTree: TreeNode[], diffInfo: DiffInfo, previewURLs: PreviewURLs, prInfo: PRInfo, warnings: RawWarning[], container: Container, diffFilterConfigData?: DiffFilterConfigData): Store => {
  const repoLink = `${appSubUrl}/${diffInfo.username}/${diffInfo.reponame}`;
  const refLinkSnippet = `commit/${diffInfo.headCommit}`;
  const isProjectView = ref(false);
  const issueLink = diffInfo.issueLink;

  // FIXME: this does not appear to need to be reactive
  const fileTree = reactive({
    items: initialFileTree,
  });

  // FIXME: this does not appear to need to be reactive
  /**
   * This is an index from hash -> path for quick lookups
   */
  const hashToPathMap = computed<Map<string, PreviewableTreeNode>>(() => {
    const map = new Map();
    // FIXME: declare this function outside of the computed
    const bfs = (children: TreeNode[]) => {
      for (const item of children) {
        if (item.ty !== 'dir') {
          map.set(item.nameHash, item);
        }

        if ('children' in item && item.children) {
          bfs(item.children as TreeNode[]);
        }
      }
    };

    bfs(fileTree.items);

    return map;
  });

  // NOTE: This does change (on the hashchange event), but this could be managed via store methods
  /**
   * This is the URL hash that is currently active
   *
   * Example: #diff-a5cc2925ca8258af241be7e5b0381edf30266302
   * Example: #diff-a5cc2925ca8258af241be7e5b0381edf30266302-issuecomment-26
   * Legacy Example: #issuecomment-26
   */
  const activePath: Ref<string> = ref('');

  const activeHash: ComputedRef<string> = computed(() => activePath.value.replace('#diff-', '').split('-')[0]);

  const pageHashToHierarchy = computed<Map<string, SchematicHierarchyGroup[]>>(() => {
    const map = new Map<string, SchematicHierarchyGroup[]>();
    const walk = (nodes: TreeNode[]) => {
      for (const item of nodes) {
        if ((item.ty === 'proj-diff' || item.ty === 'multi-page-diff') && item.hierarchy?.length) {
          for (const child of item.children ?? []) {
            map.set(child.nameHash, item.hierarchy);
          }
        }
        if (item.ty === 'dir' && item.children) {
          walk(item.children);
        }
      }
    };
    walk(fileTree.items);
    return map;
  });

  const activeHierarchy = computed<SchematicHierarchyGroup[]>(() => {
    return pageHashToHierarchy.value.get(activeHash.value) ?? [];
  });

  // FIXME: This doesn't need to be reactive (and should stay behind-the-scenes too)
  const loader: ShallowRef<Loader> = shallowRef(new Loader(previewURLs, container, diffInfo));
  const activePreview: Ref<Preview> = ref(emptyPreview());
  const suppressNextAnchorScroll: Ref<boolean> = ref(false);

  /**
   * loadPreview takes in the hash identifier of a node in the tree, and loads the corresponding preview
   */
  const loadPreview = (nodeHash: string): void => {
    // nodeHashes can be of one of two formats: <hash> or <hash>_<doc_id>.

    // search the file tree for the active hash to get information about the file
    let node = hashToPathMap.value.get(nodeHash);
    if (!node && nodeHash.includes('_')) {
      // if this nodeHash includes a doc_id, check if its parent exists
      const partialHash = nodeHash.split('_')[0];
      node = hashToPathMap.value.get(partialHash);

      // If the parent is loadable, and hasn't loaded, and add a loading message and return.
      if (node && 'loadingStatus' in node && node.loadingStatus === LOADING_STATUS.IN_PROGRESS) {
        const waitForNodeStatusChange = (node: LoadableTreeNode): Promise<void> => {
          // Wait for the node to load and then reload the preview
          return new Promise((resolve) => {
            const unwatch = watch(
              () => node.loadingStatus,
              (newStatus) => {
                if (newStatus !== LOADING_STATUS.IN_PROGRESS) {
                  unwatch();
                  resolve();
                  triggerRef(activePath);
                }
              },
            );
          });
        };
        activePreview.value = loadingPreview(node.name, waitForNodeStatusChange(node));
        return;
      }

      // If we found a node, and it's not loadable, then a snippet
      // navigation likely navigated us incorrectly. Let this branch
      // fall through, and we'll render the parent instead.
    }

    // If there is no node at this point, then the hash is not correct
    if (!node) {
      activePreview.value = errorPreview('', `Could not find file for hash ${nodeHash}`);
      return;
    }

    const preview = loader.value.GetPreview(node);
    if (preview.ty === 'loading') {
      // if the preview is loading, we need to update the loader when its resolved
      (async () => {
        await preview.previewPromise;
        triggerRef(loader);
      })();
    }

    activePreview.value = preview;

    // scroll to the element after the preview is loaded, we must wait for the next tick to ensure
    // the dom has been updated
    if (preview.ty !== 'error' && preview.ty !== 'loading') {
      nextTick(() => {
        if (suppressNextAnchorScroll.value) {
          suppressNextAnchorScroll.value = false;
          return;
        }

        const target = document.querySelector(window.location.hash);
        if (target) {
          target.scrollIntoView();
        }
      });
    }
  };

  // This ensures that the active preview is updated whenever the active path changes
  // note that since this uses side effects (loader.GetPreview) we cannot use computed
  watch([activePath, loader], () => {
    const activeFragmentIdentifier = activePath.value;

    // if we aren't looking at a file, return an empty preview
    if (activeFragmentIdentifier === '') {
      activePreview.value = emptyPreview();
      return;
    }

    // parse the fragment identifier
    const parts = activeFragmentIdentifier.replace('#', '').split('-');
    if (parts.length < 2) {
      // don't change anything, we don't have enough information they might be navigating to something on the page
      return;
    }

    switch (parts[0]) {
      case 'diff':
        loadPreview(parts[1]);
        break;
      case 'issuecomment':
        // if we see #issuecomment-27 we lookup the correct filepath for the comment and load its preview
        break;
      default:
        activePreview.value = errorPreview('', `Unknown fragment identifier ${parts[0]}`);
    }
  });

  const loadProjectPages = async (project: ProjectDiff | MultiPageDiff) => {
    const startTime = performance.now();
    project['loadingStatus'] = LOADING_STATUS.IN_PROGRESS;
    try {
      // Fetch diff JSON + convert JSON for both head and base in parallel.
      // Head convert is the primary source; base convert provides hierarchy
      // placement for deleted pages. Failures must not block the diff tree.
      const loadConvert = async (commit: string | null, path: string): Promise<Record<string, unknown> | null> => {
        if (!commit) return null;
        try {
          const result = await AllSpiceJsonLoader.load(
            container,
            AllSpiceJsonLoader.buildUrl(container, diffInfo.username, diffInfo.reponame, commit, path),
            null,
          );
          return result.headJson as Record<string, unknown> | null;
        } catch {
          return null;
        }
      };

      const [diffResults, headConvert, baseConvert] = await Promise.all([
        AllSpiceJsonLoader.load<DiffPreviewData>(
          container,
          buildDiffPreviewRoute(container, diffInfo.username, diffInfo.reponame, 'diff_page', diffInfo.headCommit, project.headPath, '', '', diffInfo.baseCommit, project.status !== FILE_DIFF_STATUS.ADDED && project.basePath),
          null,
        ),
        project.status !== FILE_DIFF_STATUS.DELETED ? loadConvert(diffInfo.headCommit, project.headPath) : Promise.resolve(null),
        project.status !== FILE_DIFF_STATUS.ADDED ? loadConvert(diffInfo.baseCommit, project.basePath) : Promise.resolve(null),
      ]);

      const diffPages = (diffResults.headJson ?? []) as {pageId: string; name: string; basePageId?: string; status?: FileDiffStatus}[];
      const convertHead = (project.status === FILE_DIFF_STATUS.DELETED ? baseConvert : headConvert) as {pages?: {id: string; name: string}[]} | null;
      const sourcePages = mergePages(diffPages, convertHead);

      project['children'] = [];
      const VIEWED_PULL_STATE = 2;
      for (const page of sourcePages) {
        const pageViewedPath = `${project.basePath}/${page.pageId}`;
        project['children'].push({
          ty: 'proj-page-diff',
          projectName: project.name,
          name: page.name,
          nameHash: `${project.nameHash}_${page.pageId}`,
          headPath: project.headPath,
          basePath: project.basePath,
          status: page.status,
          pageId: page.pageId,
          basePageId: page.basePageId,
          pageViewedPath,
          isViewed: prInfo?.filesViewedState?.[pageViewedPath] === VIEWED_PULL_STATE,
        });
      }

      project['loadingStatus'] = LOADING_STATUS.SUCCESS;

      const headPages: SchematicHierarchyInputPage[] = headConvert && Array.isArray(headConvert.pages) ? headConvert.pages as SchematicHierarchyInputPage[] : [];
      const headPageGroups = (headConvert?.page_groups ?? {}) as Record<string, SchematicPageGroup>;
      const rawBasePages: SchematicHierarchyInputPage[] = baseConvert && Array.isArray(baseConvert.pages) ? baseConvert.pages as SchematicHierarchyInputPage[] : [];
      const basePageGroups = (baseConvert?.page_groups ?? {}) as Record<string, SchematicPageGroup>;

      // Renamed pages have different IDs in head vs base. Filter them out of
      // basePages so the builder doesn't treat the old ID as a deleted page.
      const renamedBaseIds = new Set(
        sourcePages.filter((p) => p.basePageId !== p.pageId).map((p) => p.basePageId),
      );
      const basePages = rawBasePages.filter((p) => !renamedBaseIds.has(p.id));

      // A project that still has a head version needs its head convert to place
      // pages. If that fetch failed, building from base alone would mark every
      // page deleted — leave the hierarchy unset so the view falls back to a
      // flat list. A deleted project legitimately has no head convert.
      const headConvertMissing = headConvert === null && project.status !== FILE_DIFF_STATUS.DELETED;

      if (!headConvertMissing && (headPages.length > 0 || basePages.length > 0)) {
        project['hierarchy'] = buildSchematicHierarchy(
          headPages,
          headPageGroups,
          basePages,
          basePageGroups,
        );
      }

      timing('load_project_pages', performance.now() - startTime);
    } catch {
      project['loadingStatus'] = LOADING_STATUS.FAILED;
    }
  };

  watch(
    () => fileTree.items,
    (newItems) => {
      const processItems = (items: TreeNode[]) => {
        for (const item of items) {
          if (item.ty === 'proj-diff' || item.ty === 'multi-page-diff') {
            loadProjectPages(item);
          } else if (item.ty === 'dir' && item.children) {
            processItems(item.children);
          }
        }
      };

      processItems(newItems);
    },
    {immediate: true},
  );

  const initialToggle: DiffDisplayLevel =
    (diffFilterConfigData?.diffToggle && VALID_TOGGLE_KEYS.has(diffFilterConfigData.diffToggle)) ?
      diffFilterConfigData.diffToggle as DiffDisplayLevel :
      'full';

  const diffFilterConfig: DiffFilterConfig = {
    diffToggle: ref<DiffDisplayLevel>(initialToggle),
    filterOverrides: ref<Record<string, boolean>>(diffFilterConfigData?.filterOverrides || {}),
    layerOverrides: ref<Record<string, boolean>>(diffFilterConfigData?.layerOverrides || {}),
    showUnchangedSheets: ref<boolean>(diffFilterConfigData?.showUnchangedSheets ?? true),
  };

  const diffFilterHiddenIds = ref<string[]>([]);

  return {
    activeHash,
    activeHierarchy,
    activePath,
    activePreview,
    container,
    diffInfo,
    diffFilterConfig,
    fileTree,
    hashToPathMap,
    isProjectView,
    issueLink,
    loader,
    prInfo,
    repoLink,
    refLinkSnippet,
    suppressNextAnchorScroll,
    warnings,
    diffFilterHiddenIds,
  };
};

let allSpiceAppStore: null | Store = null;
export function initAllSpiceAppStore(initialFileTree: TreeNode[], diffInfo: DiffInfo, previewURLs: PreviewURLs, prInfo: PRInfo, warnings: RawWarning[], container: Container, diffFilterConfigData?: DiffFilterConfigData): Store {
  allSpiceAppStore = createStore(initialFileTree, diffInfo, previewURLs, prInfo, warnings, container, diffFilterConfigData);
  return allSpiceAppStore;
}

export function useAllSpiceAppStore(): Store {
  return allSpiceAppStore;
}
